/* Mi perfil → Datos de la empresa (logo + facturación del cliente activo; gated por rol). */
import { useEffect, useRef, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import { usePerfilCtx } from './PortalPerfilLayout'
import { IconBuilding, IconCamera } from '@/components/icons/Icons'

export default function PerfilEmpresa() {
  const api = useApi()
  const { perfil, loading, reload } = usePerfilCtx()

  const [tipoDoc, setTipoDoc] = useState('RUC')
  const [rucDni, setRucDni] = useState('')
  const [razonSocial, setRazonSocial] = useState('')
  const [dirFiscal, setDirFiscal] = useState('')
  const [saving, setSaving] = useState(false)
  const [subiendoLogo, setSubiendoLogo] = useState(false)
  const inputLogo = useRef<HTMLInputElement>(null)

  const empresaNombre = perfil?.empresa?.nombre_empresa || ''
  const puedeEditar = !!perfil?.empresa?.puede_editar_facturacion
  const miRol = perfil?.empresa?.mi_rol || ''
  const logo = perfil?.empresa?.logo_url || null

  useEffect(() => {
    const f = perfil?.empresa?.facturacion
    setTipoDoc(f?.tipo_documento_fiscal || 'RUC')
    setRucDni(f?.ruc_dni || '')
    setRazonSocial(f?.razon_social || '')
    setDirFiscal(f?.direccion_fiscal || '')
  }, [perfil])

  async function guardar() {
    if (tipoDoc === 'RUC' && rucDni.trim() && !/^\d{11}$/.test(rucDni.trim())) {
      showToast('El RUC debe tener 11 dígitos', 'error'); return
    }
    setSaving(true)
    try {
      await api.put('/api/portal/perfil', {
        tipo_documento_fiscal: tipoDoc,
        ruc_dni: rucDni.trim(),
        razon_social: razonSocial.trim(),
        direccion_fiscal: dirFiscal.trim(),
      })
      showToast('Datos de la empresa actualizados', 'success')
      reload()
    } catch (err: any) {
      showToast(err.message || 'No se pudo guardar', 'error')
    } finally { setSaving(false) }
  }

  async function subirLogo(file: File | null) {
    if (!file) return
    setSubiendoLogo(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      await api.upload('/api/portal/perfil/logo', fd)
      showToast('Logo actualizado', 'success')
      reload()
    } catch (err: any) {
      showToast(err.message || 'No se pudo subir el logo', 'error')
    } finally {
      setSubiendoLogo(false)
      if (inputLogo.current) inputLogo.current.value = ''
    }
  }

  if (loading && !perfil) return <p className="pp-cargando">Cargando…</p>

  return (
    <>
      {/* El logo va en su propia tarjeta: no es un dato de facturación, es la
          identidad de la empresa adentro del panel. */}
      <div className="card pp-card">
        <div className="pp-card__head">
          <div>
            <h3 className="pp-card__titulo">Logo de la empresa</h3>
            <p className="pp-card__sub">
              Se ve en tu panel, al pie de la barra lateral.
              {!puedeEditar && ' Solo los contactos PRINCIPAL o de FACTURACIÓN pueden cambiarlo.'}
            </p>
          </div>
        </div>

        <div className="pp-identidad">
          <button
            type="button"
            className="pp-logo"
            onClick={() => puedeEditar && inputLogo.current?.click()}
            disabled={!puedeEditar || subiendoLogo}
            title={puedeEditar ? 'Cambiar el logo' : 'No tienes permiso para cambiarlo'}
          >
            {logo
              ? <img src={logo} alt="" className="pp-logo__img" />
              : <IconBuilding size={24} />}
            {puedeEditar && <span className="pp-avatar__capa"><IconCamera size={16} /></span>}
          </button>
          <div className="pp-identidad__texto">
            <strong>{empresaNombre || 'Tu empresa'}</strong>
            <span>{subiendoLogo ? 'Subiendo…' : 'PNG, JPG o WebP, hasta 5 MB. Se guarda sin recortar.'}</span>
            {puedeEditar && (
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => inputLogo.current?.click()}
                disabled={subiendoLogo}
              >
                {logo ? 'Cambiar logo' : 'Subir logo'}
              </button>
            )}
          </div>
          <input
            ref={inputLogo}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="pp-input-oculto"
            onChange={e => subirLogo(e.target.files?.[0] ?? null)}
          />
        </div>
      </div>

      <div className="card pp-card">
        <div className="pp-card__head">
          <div>
            <h3 className="pp-card__titulo">
              Datos de facturación{empresaNombre ? `: ${empresaNombre}` : ''}
            </h3>
            <p className="pp-card__sub">
              Se usan para emitir las boletas y facturas electrónicas (SUNAT) de esta empresa.
              {!puedeEditar && ' Solo los contactos PRINCIPAL o de FACTURACIÓN pueden editarlos.'}
            </p>
          </div>
          {miRol && <span className="pp-card__rol">Tu rol: {miRol}</span>}
        </div>

        <div className="pp-grid-campos">
          <div className="form-group">
            <label className="form-label">Tipo de documento</label>
            <select className="form-input" value={tipoDoc} disabled={!puedeEditar} onChange={e => setTipoDoc(e.target.value)}>
              <option value="RUC">RUC (factura)</option>
              <option value="DNI">DNI (boleta)</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{tipoDoc === 'DNI' ? 'DNI' : 'RUC'}</label>
            <input className="form-input" value={rucDni} disabled={!puedeEditar} onChange={e => setRucDni(e.target.value)} placeholder={tipoDoc === 'DNI' ? '8 dígitos' : '20XXXXXXXXX'} />
          </div>
          <div className="form-group pp-ancho">
            <label className="form-label">Razón social {tipoDoc === 'DNI' ? '/ Nombre' : ''}</label>
            <input className="form-input" value={razonSocial} disabled={!puedeEditar} onChange={e => setRazonSocial(e.target.value)} placeholder="Como aparece en el comprobante" />
          </div>
          <div className="form-group pp-ancho">
            <label className="form-label">Dirección fiscal</label>
            <input className="form-input" value={dirFiscal} disabled={!puedeEditar} onChange={e => setDirFiscal(e.target.value)} placeholder="Dirección registrada en SUNAT" />
          </div>
        </div>

        {puedeEditar && (
          <div className="pp-card__pie">
            <button className="btn btn-primary" onClick={guardar} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar datos de la empresa'}
            </button>
          </div>
        )}
      </div>
    </>
  )
}
